import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Typography,
  Grid,
  Pagination,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import CrewMemberCard from "./CrewMemberCard";
import type { CrewMember, CrewMembersResponse } from "./site-visit.types";

interface CrewMemberListProps {
  selectedMembers: CrewMember[];
  onSelectMember: (member: CrewMember) => void;
  onDeselectMember: (member: CrewMember) => void;
  apiService: {
    get: <T>(url: string) => Promise<{ data: T }>;
  };
}

const PAGE_SIZE = 10;

const CrewMemberList: React.FC<CrewMemberListProps> = ({
  selectedMembers,
  onSelectMember,
  onDeselectMember,
  apiService,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [crewMembers, setCrewMembers] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchCrewMembers = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/crews/crew-members/?page=${page}&page_size=${PAGE_SIZE}`;

      if (searchTerm.trim().length >= 3) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }

      const response = await apiService.get<CrewMembersResponse>(url);
      setCrewMembers(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error("Failed to load crew members:", error);
      setCrewMembers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, page, apiService]);

  // Initial fetch
  useEffect(() => {
    fetchCrewMembers();
  }, [fetchCrewMembers]);

  // Debounced search
  useEffect(() => {
    if (searchTerm.trim().length === 0 || searchTerm.trim().length >= 3) {
      const debounce = setTimeout(() => {
        setPage(1);
        fetchCrewMembers();
      }, 400);
      return () => clearTimeout(debounce);
    }
  }, [searchTerm, fetchCrewMembers]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
  };

  const isSelected = (memberId: number): boolean => {
    return selectedMembers.some((m) => m.id === memberId);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <Box>
      {/* Search Input */}
      <TextField
        fullWidth
        placeholder="Search crew member..."
        helperText="Type at least 3 characters to search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Empty State */}
      {!loading && crewMembers.length === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <Typography color="text.secondary">No crew members found</Typography>
        </Box>
      )}

      {/* Crew Member Grid */}
      {!loading && crewMembers.length > 0 && (
        <>
          <Grid container spacing={2}>
            {crewMembers.map((member) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={member.id}>
                <CrewMemberCard
                  crewMember={member}
                  isSelected={isSelected(member.id)}
                  onSelect={onSelectMember}
                  onDeselect={onDeselectMember}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CrewMemberList;
